import React from 'react';
import { Select, Button } from 'antd';
import { message } from '../../utils/antdMessage';
import { VideoCameraOutlined } from '@ant-design/icons';
import { PROTOCOLS, getLayoutSlotCount, getCellGridStyle } from '../../utils/multiWindowLive';
import MultiWindowVideoPlayer from './MultiWindowVideoPlayer';

const { Option } = Select;

/** Prefer bound member display name; never lead with raw stream_key. */
/**
 * Larix 推流名常 = 绑定令牌 = devices.stream_key（一码通吃）。
 * 若能匹配到队员，显示 Locus/指挥端 队员名；否则「未绑定」+ 短码，绝不单独展示 stream_key。
 */
function resolveStreamOptionLabel(streamKey, memberMetaByKey = {}) {
  const key = String(streamKey || '').trim();
  const meta = memberMetaByKey[key]
    || memberMetaByKey[streamKey]
    || memberMetaByKey[key.replace(/\s+/g, '')];
  let name = String(meta?.name || '').trim();
  // If device.name was mistakenly the raw token/stream_key, do not treat as 队员名
  if (name && (name === key || /^[a-f0-9]{16,64}$/i.test(name))) {
    name = '';
  }
  if (name) return name;
  if (!key) return '未绑定';
  const short = key.length > 10 ? `${key.slice(0, 8)}…` : key;
  return `未绑定 · ${short}`;
}

/**
 * 多窗口播放器网格（等分 / 自定义跨度）
 */
export default function PlayerGrid({
  effectiveLayout,
  isMobileLayout,
  streams,
  availableStreams,
  memberMetaByKey,
  streamStates,
  dragFromIndex,
  setDragFromIndex,
  dragOverIndex,
  setDragOverIndex,
  fullscreenPlayer,
  setFullscreenPlayer,
  addStream,
  removeStream,
  updateStreamProtocol,
  moveStreamBetweenSlots
}) {
  const { cols, rows, mode, cells, fitScreen } = effectiveLayout;
  const maxStreams = getLayoutSlotCount(effectiveLayout);
  const isAreas = mode === 'areas' && Array.isArray(cells) && !isMobileLayout;
  const mobileFit = isMobileLayout && fitScreen;
  // 手机竖排：每行至少占一定视口高度，避免 minmax(0,1fr) 在父级高度未算准时被压成一条线
  const mobileRowMinVh = Math.max(18, Math.min(36, Math.floor(72 / Math.max(rows, 1))));

  return (
    <div
      className={[
        'player-grid',
        isMobileLayout ? 'player-grid--mobile' : '',
        mobileFit ? 'player-grid--fit' : '',
        isAreas ? 'player-grid--areas' : ''
      ].filter(Boolean).join(' ')}
      style={{
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: mobileFit
          ? `repeat(${rows}, minmax(${mobileRowMinVh}vh, 1fr))`
          : (isMobileLayout
            ? `repeat(${rows}, minmax(160px, 36vh))`
            : `repeat(${rows}, 1fr)`)
      }}
    >
      {Array.from({ length: maxStreams }, (_, index) => {
        const stream = streams[index];
        const cell = isAreas ? cells[index] : null;
        const cellStyle = getCellGridStyle(cell);
        const isMain = !!(cell && cell.main);
        const isDragOver = dragOverIndex === index && dragFromIndex !== null && dragFromIndex !== index;

        const slotDropProps = {
          onDragOver: (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (dragOverIndex !== index) setDragOverIndex(index);
          },
          onDragLeave: (e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setDragOverIndex((cur) => (cur === index ? null : cur));
            }
          },
          onDrop: (e) => {
            e.preventDefault();
            const raw = e.dataTransfer.getData('text/plain');
            const from = raw !== '' ? parseInt(raw, 10) : dragFromIndex;
            setDragFromIndex(null);
            setDragOverIndex(null);
            if (Number.isInteger(from)) moveStreamBetweenSlots(from, index);
          }
        };

        if (!stream) {
          return (
            <div
              key={`empty-${index}`}
              className={[
                'empty-player-slot',
                isMain ? 'player-slot--main' : '',
                isDragOver ? 'player-slot--drag-over' : ''
              ].filter(Boolean).join(' ')}
              style={cellStyle}
              {...slotDropProps}
            >
              <div className="empty-content">
                <VideoCameraOutlined style={{ fontSize: isMain ? 56 : 48, color: '#d9d9d9' }} />
                <div className="empty-text">
                  {isDragOver ? '放到此窗口' : (isMain ? '主画面（空）' : '空窗口')}
                </div>
                {availableStreams.length > 0 ? (
                  <>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>
                      可用流: {availableStreams.length}个
                    </div>
                    <Select
                      placeholder="选择流开始播放"
                      style={{ width: 150 }}
                      onSelect={(value) => {
                        const selectedStream = availableStreams.find(s => s.streamKey === value);
                        if (selectedStream) {
                          if (streams[index] && streams[index] !== null) {
                            message.warning('此窗口已有流，请先移除现有流');
                            return;
                          }
                          const originalStreamKey = value;
                          const displayName = resolveStreamOptionLabel(value, memberMetaByKey);
                          setTimeout(() => {
                            addStream(originalStreamKey, displayName, 'webrtc', index);
                          }, 50);
                        }
                      }}
                    >
                      {availableStreams.map(s => (
                        <Option key={s.streamKey} value={s.streamKey}>
                          {resolveStreamOptionLabel(s.streamKey, memberMetaByKey)}
                        </Option>
                      ))}
                    </Select>
                  </>
                ) : (
                  <div style={{ fontSize: '12px', color: '#888' }}>
                    {isDragOver ? '松开鼠标移入画面' : '暂无可用流 · 可拖入其他窗口画面'}
                  </div>
                )}
              </div>
            </div>
          );
        }

        return (
          <div
            key={stream.id || `${stream.streamKey}_${index}`}
            className={[
              'player-slot',
              isMain ? 'player-slot--main' : '',
              dragFromIndex === index ? 'player-slot--dragging' : '',
              isDragOver ? 'player-slot--drag-over' : ''
            ].filter(Boolean).join(' ')}
            style={cellStyle}
            {...slotDropProps}
          >
            <MultiWindowVideoPlayer
              key={stream.id || `${stream.streamKey}_${index}`}
              streamKey={stream.streamKey}
              memberName={stream.memberName}
              protocol={stream.protocol}
              hideOverlayControls={isMobileLayout}
              batteryLevel={
                memberMetaByKey[stream.streamKey]?.batteryLevel
                ?? memberMetaByKey[stream.memberName]?.batteryLevel
                ?? null
              }
              lastSeen={
                memberMetaByKey[stream.streamKey]?.lastSeen
                ?? memberMetaByKey[stream.memberName]?.lastSeen
                ?? null
              }
              slotDragIndex={index}
              onSlotDragStart={(from) => setDragFromIndex(from)}
              onSlotDragEnd={() => {
                setDragFromIndex(null);
                setDragOverIndex(null);
              }}
              onError={(protocol, error) => {
                console.error(`${stream.streamKey} ${protocol}播放失败:`, error);
                setTimeout(() => {
                  console.log(`${stream.streamKey} 播放失败，允许重新尝试`);
                }, 2000);
              }}
              onSuccess={(protocol) => {
                console.log(`${stream.streamKey} ${protocol}播放成功`);
              }}
              onStop={() => {
                console.log(`${stream.streamKey} 停止播放`);
                removeStream(index);
              }}
              isFullscreen={fullscreenPlayer === index}
              onFullscreenChange={(isFullscreen) => {
                setFullscreenPlayer(isFullscreen ? index : null);
              }}
              forceMuted={streamStates.get(stream.id)?.isMuted || false}
            />

            <div
              className="player-actions"
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
            >
              {isMain && (
                <span className="main-slot-badge" title="主画面">主</span>
              )}
              <Select
                value={stream.protocol}
                onChange={(protocol) => updateStreamProtocol(index, protocol)}
                size="small"
                style={{ width: 80 }}
              >
                {Object.entries(PROTOCOLS).map(([key, config]) => (
                  <Option key={key} value={key}>
                    <span style={{ color: config.color }}>{config.name}</span>
                  </Option>
                ))}
              </Select>

              <Button
                type="text"
                size="small"
                danger
                onClick={() => removeStream(index)}
              >
                移除
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
